// Copied from platform-app components/pos/layout/HeaderPreview.tsx on 2026-10-18 for Super Admin → POS Defaults — keep in step with the original.
import React from 'react';
import { ChevronLeft, ChevronRight, EyeOff, Lock, Palette, Search, ShieldCheck } from 'lucide-react';
import { HEADER_BUTTON_REGISTRY, mergeButton, type HeaderButtonConfig } from '../../../config/buttonRegistry';
import { hasUserColours, resolveButtonLook } from '../../../config/buttonTheme';
import { getIcon } from '../../../utils/iconRegistry';
import { PREVIEW_PALETTE, type PreviewTheme } from './previewPalette';

export interface HeaderPreviewProps {
  buttons: HeaderButtonConfig[];
  onCustomize: (id: HeaderButtonConfig['id']) => void;
  onMove: (id: HeaderButtonConfig['id'], dir: -1 | 1) => void;
  theme?: PreviewTheme;
}

/**
 * The till's sale-screen header row — Scannable / Non-scannable switch, the
 * header icon buttons, then the search field — drawn in the chosen POS theme.
 * Header tiles default to the search-field surface (as the till paints them);
 * merchant colours replace it. Hidden buttons are shown greyed in the list below,
 * not in the row, matching what the cashier will see.
 */
export default function HeaderPreview({ buttons, onCustomize, onMove, theme = 'light' }: HeaderPreviewProps) {
  const p = PREVIEW_PALETTE[theme];

  const look = (b: HeaderButtonConfig) => {
    if (hasUserColours(b)) return resolveButtonLook(b.id, b, theme, 'default', HEADER_BUTTON_REGISTRY[b.id]);
    return { style: { background: p.input, color: p.tx, border: '1px solid transparent' } as React.CSSProperties, fg: p.tx };
  };
  const appearance = (b: HeaderButtonConfig) => mergeButton(b.id, b, HEADER_BUTTON_REGISTRY[b.id])!;
  const shown = buttons.filter((b) => b.active !== false);

  return (
    <div className="space-y-5">
      {/* Live header row */}
      <div data-testid="header-preview" className="overflow-hidden rounded-[14px] border border-stone-300">
        <div className="flex items-center gap-2.5 px-3" style={{ height: 64, background: p.head, borderBottom: `1px solid ${p.headBor}` }}>
          <div className="flex h-11 shrink-0 overflow-hidden rounded-xl" style={{ background: p.input }}>
            <span className="flex items-center rounded-[11px] px-3.5 text-[13px] font-semibold" style={{ background: p.addBg, color: p.addTx }}>Scannable</span>
            <span className="flex items-center px-3.5 text-[13px] font-semibold" style={{ color: p.mu }}>Non-scannable</span>
          </div>
          {shown.map((b) => {
            const a = appearance(b);
            const l = look(b);
            const Icon = getIcon(a.icon);
            return (
              <button
                key={b.id}
                type="button"
                data-testid={`header-tile-${b.id}`}
                onClick={() => onCustomize(b.id)}
                title={`${a.label} — click to customize`}
                aria-label={`Customize ${a.label}`}
                className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition-transform hover:scale-105"
                style={l.style}
              >
                <Icon size={22} color={l.fg} />
                {b.id === 'open-order' && (
                  <span className="absolute -right-1.5 -top-1.5 flex h-[20px] min-w-[20px] items-center justify-center rounded-full px-1 text-[11px] font-bold text-white" style={{ background: '#e11d48', border: `2px solid ${p.head}` }}>2</span>
                )}
              </button>
            );
          })}
          <div className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl px-3.5" style={{ background: p.input }}>
            <Search size={16} color={p.inputTx} />
            <span className="truncate text-[13px]" style={{ color: p.inputTx }}>Search by name, SKU, or scan barcode...</span>
          </div>
        </div>
        <div className="h-10" style={{ background: p.body }} />
      </div>

      {/* Button list — order, status, customize */}
      <div>
        <span className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-stone-500">
          Header buttons
          <span className="ml-1 text-[10px] normal-case text-stone-400">(left to right on the till · click a row to customize)</span>
        </span>
        <ol className="divide-y divide-stone-100 overflow-hidden rounded-xl border border-stone-200">
          {buttons.map((b, i) => {
            const a = appearance(b);
            const l = look(b);
            const Icon = getIcon(a.icon);
            const hidden = b.active === false;
            return (
              <li key={b.id} data-testid={`header-row-${b.id}`} className={`flex items-center gap-3 bg-white px-3 py-2 ${hidden ? 'opacity-60' : ''}`}>
                <span className="w-4 text-center text-xs font-semibold tabular-nums text-stone-400">{i + 1}</span>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg" style={l.style}>
                  <Icon size={20} color={l.fg} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-stone-800">{a.label}</div>
                  <div className="flex flex-wrap gap-1.5 pt-0.5">
                    {hidden && <Chip icon={<EyeOff size={11} />} text="Hidden" />}
                    {b.requiresApproval && <Chip icon={<Lock size={11} />} text="Manager approval" />}
                    {b.visibility && b.visibility !== 'all' && (
                      <Chip icon={<ShieldCheck size={11} />} text={b.visibility === 'admin' ? 'Manager only' : 'Employee only'} />
                    )}
                    {!hidden && !b.requiresApproval && (!b.visibility || b.visibility === 'all') && (
                      <span className="text-[11px] text-stone-400">Everyone</span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  aria-label={`Move ${a.label} left`}
                  disabled={i === 0}
                  onClick={() => onMove(b.id, -1)}
                  className="flex h-11 w-11 items-center justify-center rounded-lg border border-stone-200 text-stone-500 hover:bg-stone-50 disabled:opacity-30"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  aria-label={`Move ${a.label} right`}
                  disabled={i === buttons.length - 1}
                  onClick={() => onMove(b.id, 1)}
                  className="flex h-11 w-11 items-center justify-center rounded-lg border border-stone-200 text-stone-500 hover:bg-stone-50 disabled:opacity-30"
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  type="button"
                  data-testid={`header-customize-${b.id}`}
                  onClick={() => onCustomize(b.id)}
                  className="flex min-h-[44px] items-center gap-1.5 rounded-lg border border-stone-200 px-3 text-xs font-semibold text-stone-700 hover:bg-stone-50"
                >
                  <Palette size={14} />
                  Customize
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}

function Chip({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-medium text-stone-600">
      {icon}
      {text}
    </span>
  );
}
